import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { goBackOrParent } from "@/lib/nav";
import { AppShell } from "@/components/AppShell";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useRefetchOnActive } from "@/lib/useRefetch";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { ProgressRing } from "@/components/ProgressRing";
import { ReportCard } from "@/components/ReportCard";
import { NudgeSheet } from "@/components/NudgeSheet";
import { timeAgo } from "@/lib/format";
import { ArrowLeft, MapPin, Mail, Eye, Plus, Trash2, Loader2, ChevronRight, ChevronDown, Check, MoreVertical, RotateCcw, FileText, Archive, Sparkles, AlertTriangle } from "lucide-react";

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [delReport, setDelReport] = useState(null);
  const [tab, setTab] = useState("active");
  const [nameOpen, setNameOpen] = useState(false);
  const [reportName, setReportName] = useState("");
  const [creating, setCreating] = useState(false);
  const [nudgeReport, setNudgeReport] = useState(null);

  const applyData = useCallback((data) => {
    setJob(data);
    setReports(data.reports || []);
    setName(data.name); setLocation(data.location || "");
  }, []);

  const load = useCallback(async () => {
    try {
      // Prefer the server when online so progress wheels never show a STALE cached
      // percentage after verifying items in the editor and navigating back here.
      if (navigator.onLine) {
        const res = await api.raw.get(`/jobs/${id}`);
        if (res.data) { applyData(res.data); api.getCached(`/jobs/${id}`).catch(() => {}); return; }
      }
      const { data } = await api.getCached(`/jobs/${id}`, applyData);
      if (data) applyData(data);
      else { toast.error("Job not found"); navigate("/dashboard"); }
    } catch {
      try {
        const { data } = await api.getCached(`/jobs/${id}`, applyData);
        if (data) applyData(data);
        else { toast.error("Job not found"); navigate("/dashboard"); }
      } catch { toast.error("Job not found"); navigate("/dashboard"); }
    } finally { setLoading(false); }
  }, [id, navigate, applyData]);

  useEffect(() => { load(); }, [load]);
  useRefetchOnActive(load);

  const patch = async (fields) => {
    try { await api.patch(`/jobs/${id}`, fields); } catch { toast.error("Save failed"); }
  };

  const setComplete = async () => {
    const next = job.status === "completed" ? "open" : "completed";
    setJob({ ...job, status: next });
    await patch({ status: next });
  };
  const onMarkComplete = () => {
    if (job.status !== "completed" && reports.length === 0) { setConfirmComplete(true); return; }
    setComplete();
  };

  const openNewReport = () => {
    setReportName(`Report ${reports.length + 1}`);
    setNameOpen(true);
  };

  const createReport = async () => {
    const title = reportName.trim() || `Report ${reports.length + 1}`;
    setCreating(true);
    try {
      const { data } = await api.post(`/jobs/${id}/reports`, { title });
      setNameOpen(false);
      setCreating(false);
      navigate(`/jobs/${id}/reports/${data.id}`);
    } catch (e) {
      setCreating(false);
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402 && typeof d === "string" && d.startsWith("sandbox")) {
        window.dispatchEvent(new Event("sp:convert"));
      } else {
        toast.error("Could not create report");
      }
    }
  };

  const del = async () => {
    try { await api.delete(`/jobs/${id}`); toast.success("Job deleted"); navigate("/dashboard"); }
    catch { toast.error("Delete failed"); }
  };

  const shareReport = async (r) => {
    try {
      const { data } = await api.post(`/reports/${r.id}/share`);
      const url = data.url || `${window.location.origin}/api/s/${data.token}`;
      const body = `Punch list — ${r.title}.\n\nOpen the link, complete each item, attach proof photos, then send it back.`;
      if (r.status === "draft") {
        setReports((arr) => arr.map((x) => (x.id === r.id ? { ...x, status: "sent" } : x)));
        api.post(`/reports/${r.id}/mark-sent`).catch(() => {});
      }
      if (navigator.share) { try { await navigator.share({ title: r.title, text: body, url }); return; } catch { /* cancelled */ } }
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — send it to whoever's doing the work");
    } catch { toast.error("Could not create share link"); }
  };

  const confirmDeleteReport = async () => {
    const r = delReport;
    setDelReport(null);
    setReports((arr) => arr.filter((x) => x.id !== r.id));
    try { await api.delete(`/reports/${r.id}`); toast.success("Report deleted"); }
    catch { toast.error("Delete failed"); load(); }
  };

  const archiveReport = async (r) => {
    const val = !r.archived;
    setReports((arr) => arr.map((x) => (x.id === r.id ? { ...x, archived: val } : x)));
    try { await api.patch(`/reports/${r.id}`, { archived: val }); toast.success(val ? "Report archived" : "Report restored"); }
    catch { toast.error("Couldn't update — try again"); load(); }
  };

  if (loading || !job) return (
    <AppShell title="Job">
      <div className="space-y-3 px-4 pt-5 sm:px-8" data-testid="jobdetail-skeleton">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-32" />
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-2xl border border-border bg-card p-4">
            <Skeleton className="h-5 w-44" />
            <Skeleton className="mt-3 h-3 w-28" />
            <Skeleton className="mt-3 h-2 w-full rounded-full" />
          </div>
        ))}
      </div>
    </AppShell>
  );

  const active = reports.filter((r) => !r.archived);
  const archived = reports.filter((r) => r.archived);
  const tidyList = active.filter((r) => (r.progress?.total || 0) > 0 && (r.progress?.pct || 0) >= 100);
  const tidyCount = tidyList.length;
  const tidyUp = async () => {
    setReports((arr) => arr.map((x) => (tidyList.some((t) => t.id === x.id) ? { ...x, archived: true } : x)));
    try { await Promise.all(tidyList.map((r) => api.patch(`/reports/${r.id}`, { archived: true }))); toast.success(`Archived ${tidyCount} report${tidyCount > 1 ? "s" : ""}`); }
    catch { toast.error("Couldn't archive — try again"); load(); }
  };
  const orderReports = (arr) => {
    const childrenByParent = {};
    const children = arr.filter((r) => r.parent_report_id);
    const nonChildren = arr.filter((r) => !r.parent_report_id);
    for (const c of children) (childrenByParent[c.parent_report_id] ||= []).push(c);
    const nonChildIds = new Set(nonChildren.map((r) => r.id));
    const ordered = [];
    for (const r of nonChildren) {
      ordered.push(r);
      if (childrenByParent[r.id]) ordered.push(...childrenByParent[r.id]);
    }
    for (const c of children) if (!nonChildIds.has(c.parent_report_id)) ordered.push(c);
    const hasChildren = new Set(Object.keys(childrenByParent).filter((pid) => nonChildIds.has(pid)));
    return { ordered, hasChildren };
  };
  const activeOrdered = orderReports(active);
  const archivedOrdered = orderReports(archived);
  const cardFor = (r, isMaster) => (
    <ReportCard key={r.id} report={r} isMaster={isMaster} onOpen={() => navigate(`/jobs/${id}/reports/${r.id}`)} onShare={() => shareReport(r)} onArchive={() => archiveReport(r)} onDelete={() => setDelReport(r)} onNudge={() => setNudgeReport(r)} />
  );

  return (
    <AppShell title={name || job?.name} onBack={() => goBackOrParent(navigate, "/dashboard")}>
      <div className="px-4 pt-4 sm:px-8">
        {/* Title + overflow menu */}
        <div className="mt-2 flex items-start gap-2">
          <Input
            data-testid="job-name-edit"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => name !== job.name && patch({ name })}
            className="h-auto flex-1 border-0 px-0 font-heading text-2xl font-extrabold shadow-none focus-visible:ring-0"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="job-menu-btn" aria-label="More actions" className="mt-1 flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-muted-foreground hover:text-foreground">
                <MoreVertical className="h-[18px] w-[18px]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem data-testid="menu-toggle-complete" onClick={onMarkComplete}>
                {job.status === "completed" ? <><RotateCcw className="mr-2 h-4 w-4" /> Reopen job</> : <><Check className="mr-2 h-4 w-4" /> Mark complete</>}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-delete-job" onClick={() => setConfirmDelete(true)} className="text-rose-600 focus:text-rose-600">
                <Trash2 className="mr-2 h-4 w-4" /> Delete job
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Address (quiet, one line) */}
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4 shrink-0" />
          <Input
            data-testid="job-location-edit"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            onBlur={() => location !== (job.location || "") && patch({ location })}
            placeholder="Add location"
            className="h-8 border-0 px-0 text-sm shadow-none focus-visible:ring-0"
          />
        </div>
        {job.assignee_email && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Mail className="h-4 w-4" /> {job.assignee_email}
          </div>
        )}

        {/* Status */}
        <div className="mt-4 flex items-center gap-3">
          <StatusPill status={job.status} />
        </div>

        {job.report_count > 0 && (
          <div className="mt-4 rounded-2xl border border-border bg-card p-4" data-testid="job-progress">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Progress</span>
              {(job.issue_count || 0) > 0 ? (
                <span className="inline-flex items-center gap-1 text-sm font-bold text-rose-500" data-testid="job-issue-count">
                  <AlertTriangle className="h-3.5 w-3.5" /> {job.issue_count} flagged
                </span>
              ) : (
                <span className="text-sm font-bold text-emerald-600">{job.progress || 0}%</span>
              )}
            </div>
            <div className="mt-2 flex h-2 w-full overflow-hidden rounded-full bg-muted">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100 - (job.issue_pct || 0), job.progress || 0)}%` }} />
              <div className="h-full bg-rose-500 transition-all" style={{ width: `${job.issue_pct || 0}%` }} />
            </div>
          </div>
        )}

        {/* Primary CTA — always at the top of Reports */}
        <Button
          data-testid="new-report-btn"
          onClick={openNewReport}
          className="mt-6 h-12 w-full gap-2 rounded-xl bg-emerald-500 text-base font-bold text-white shadow-lg shadow-emerald-500/30 hover:bg-emerald-600"
        >
          <Plus className="h-5 w-5" /> New Report
        </Button>

        {/* Active / Archive pills with counts */}
        {reports.length > 0 && (
          <div className="mt-4 flex gap-2.5" data-testid="report-tabs">
            <button
              data-testid="tab-active"
              onClick={() => setTab("active")}
              className={cn("flex flex-1 items-center justify-center gap-1.5 rounded-full py-3 text-sm font-semibold transition-colors", tab === "active" ? "bg-emerald-500 text-white" : "border border-border bg-card text-muted-foreground")}
            >
              <FileText className="h-4 w-4" /> Active ({active.length})
            </button>
            <button
              data-testid="tab-archive"
              onClick={() => setTab("archive")}
              className={cn("flex flex-1 items-center justify-center gap-1.5 rounded-full py-3 text-sm font-semibold transition-colors", tab === "archive" ? "bg-emerald-500 text-white" : "border border-border bg-card text-muted-foreground")}
            >
              <Archive className="h-4 w-4" /> Archive ({archived.length})
            </button>
          </div>
        )}

        {/* Tidy Up — only when there's something to tidy */}
        {tab === "active" && tidyCount > 0 && (
          <button
            data-testid="tidy-up-btn"
            onClick={tidyUp}
            className="mt-3 flex w-full items-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-2.5 text-left text-[13px] font-semibold text-emerald-600 dark:text-emerald-400"
          >
            <Sparkles className="h-4 w-4 shrink-0" /> Tidy Up — move {tidyCount} completed to Archive
          </button>
        )}

        {/* List */}
        <div className="mt-4 space-y-2.5 pb-8">
          {tab === "active" ? (
            active.length === 0 ? (
              <div className="flex flex-col items-center justify-center px-6 py-16 text-center" data-testid="reports-empty">
                <FileText className="h-16 w-16 text-muted-foreground/40" />
                <h3 className="mt-4 font-heading text-lg font-bold">{reports.length === 0 ? "No Reports Yet" : "All Caught Up"}</h3>
                <p className="mt-1 max-w-xs text-sm text-muted-foreground">
                  {reports.length === 0
                    ? "Create a new report to document your work with photos and proof."
                    : "No active reports. Create a new one to start documenting."}
                </p>
              </div>
            ) : (
              activeOrdered.ordered.map((r) => cardFor(r, activeOrdered.hasChildren.has(r.id)))
            )
          ) : archived.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center" data-testid="archive-empty">
              <Archive className="h-16 w-16 text-muted-foreground/40" />
              <h3 className="mt-4 font-heading text-lg font-bold">No Archived Reports</h3>
              <p className="mt-1 max-w-xs text-sm text-muted-foreground">Archive 100%-complete reports to tidy up your active list.</p>
            </div>
          ) : (
            <div className="space-y-2.5 opacity-80">{archivedOrdered.ordered.map((r) => cardFor(r, archivedOrdered.hasChildren.has(r.id)))}</div>
          )}
        </div>
      </div>

      {/* Name new report */}
      <Dialog open={nameOpen} onOpenChange={(o) => { if (!creating) setNameOpen(o); }}>
        <DialogContent className="max-w-sm" data-testid="name-report-modal">
          <DialogHeader>
            <DialogTitle>Name this report</DialogTitle>
            <DialogDescription>Give it a name so it's easy to find later — e.g. "Kitchen punch list" or "Unit 4B walkthrough".</DialogDescription>
          </DialogHeader>
          <Input
            data-testid="report-name-input"
            autoFocus
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
            onFocus={(e) => e.target.select()}
            onKeyDown={(e) => { if (e.key === "Enter" && !creating) createReport(); }}
            placeholder="Report name"
            className="h-12 text-base"
          />
          <DialogFooter className="mt-1 gap-2 sm:gap-2">
            <Button variant="outline" data-testid="report-name-cancel" onClick={() => setNameOpen(false)} disabled={creating} className="flex-1">Cancel</Button>
            <Button data-testid="report-name-create" onClick={createReport} disabled={creating} className="flex-1 gap-1 bg-emerald-500 hover:bg-emerald-600">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" /> Create</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this job?</AlertDialogTitle>
            <AlertDialogDescription>This removes the job and all its reports and photos. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm" onClick={del} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Complete-with-no-reports confirm */}
      <AlertDialog open={confirmComplete} onOpenChange={setConfirmComplete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>No reports on this job</AlertDialogTitle>
            <AlertDialogDescription>There are no reports here yet. Complete anyway?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="complete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="complete-confirm" onClick={setComplete} className="bg-emerald-500 hover:bg-emerald-600">Complete anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete report confirm */}
      <AlertDialog open={!!delReport} onOpenChange={(o) => !o && setDelReport(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this report?</AlertDialogTitle>
            <AlertDialogDescription>“{delReport?.title || "Untitled report"}” and its photos will be removed. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="report-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="report-delete-confirm" onClick={confirmDeleteReport} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NudgeSheet
        open={!!nudgeReport}
        onOpenChange={(o) => !o && setNudgeReport(null)}
        reportId={nudgeReport?.id}
        jobName={name || job?.name}
        location={location}
        dueDate={nudgeReport?.due_date}
      />
    </AppShell>
  );
}
